<template>
  <div>
    <div style="display: flex;">
      <v-btn
        v-if="showExtractCsvBtn"
        color="red"
        class="export-btn ml-auto"
        @click="onExtractToCsvClick"
      >
        {{ $t('csv.exportCsv') }}
        <v-icon
          right
          light
          red
        >
          mdi-cloud-download
        </v-icon>
      </v-btn>
    </div>
    <v-skeleton-loader
      v-if="initialLoading"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <TableRemote
      v-else
      :items="displayedWorkOrders"
      :title="$t('workOrders.pageName')"
      :isSelectable="hasPermission('workOrders.view')"
      :sortableColumns="sortableColumns"
      :isCustomIcon="true"
      :headers="tableHeaders"
      :loading="loading"
      :itemActions="actions"
      :pagination="pagination"
      hideDefaultFooter
      :isInfinitScroll="true"
      :isSearchable="false"
      :centerColumns="['action']"
      :notSortableColumns="['action', 'associatedMeasures']"
      :dataCy="dataCy.workOrders.list"
      @sendQueryParams="fetchQuery"
      @send-item="onClickRedirect"
      @iconAction="onClickAction"
    >
      <template #item.siteName="{ item }">
        <span> {{ item.siteName }}</span>
      </template>

      <template #item.completionStatus="{ item }">
        <v-icon
          v-if="(item.completionStatus === status.TODO)"
          color="primary"
          class="completionStatusIcon material-icons"
        >
          panorama_fish_eye
        </v-icon>
        <v-icon
          v-if="item.completionStatus === status.IN_PROGRESS"
          color="primary"
          class="completionStatusIcon material-icons"
        >
          timelapse
        </v-icon>
        <v-icon
          v-if="item.completionStatus === status.PENDING"
          color="primary"
          class="completionStatusIcon material-icons"
        >
          mdi-clock-outline
        </v-icon>
        <v-icon
          v-if="item.completionStatus === status.LATE"
          color="primary"
          class="completionStatusIcon material-icons"
        >
          mdi-alert-circle-outline
        </v-icon>
        <v-icon
          v-if="(item.completionStatus === status.COMPLETED)"
          color="primary"
          class="completionStatusIcon material-icons"
        >
          check_circle_outline
        </v-icon>
      </template>
      <template #item.workHours="{item}">
        {{ item.onSiteDurationHours }}
      </template>
      <template #item.assignedTo="{ item }">
        <span> {{ item.assignedTo }} </span>
      </template>
      <template
        #item.locked="{ item }"
      >
        <div
          v-if="(item.locked)"
          class="d-flex justify-center"
        >
          <v-tooltip
            right
          >
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                larg
                color="gray"
                v-bind="attrs"
                v-on="on"
                @mouseover="showLockedBy(item)"
              >
                mdi-lock
              </v-icon>
            </template>
            <span>{{ `${$t('workOrderPage.lockedBy')} ${lockedByName}` }}</span>
          </v-tooltip>
        </div>
      </template>
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
      <template #SearchWithFilters>
        <SearchWithFilters
          :filters="filters"
          :searchPlaceholder="$t('laboratory.placeholder.searchBy')"
          :filterName="filterName"
          :saveFilter="true"
          @onApplyFilters="workOrdersChanged"
          @onSearch="handleSearch"
        />
      </template>
      <template
        #item.associatedMeasures="{ item }"
      >
        <div
          style="display: flex;"
        >
          <img
            v-if="item.hasFlowMeter"
            src="/assets/meter.svg"
            class="iconAssociatedMeasures"
          >
          <img
            v-if="item.hasProductLevel"
            src="/assets/productLevel.svg"
            class="iconAssociatedMeasures"
          >
          <img
            v-if="item.hasAnalysisRequest"
            src="/assets/analysisRequest.svg"
            class="iconAssociatedMeasures"
          >
          <img
            v-if="item.hasInventory"
            src="/assets/inventory.svg"
            class="iconAssociatedMeasures"
          >
        </div>
      </template>
    </TableRemote>
  </div>
</template>

<script>
import Vue from 'vue';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import completionStatus from '@/constants/workOrderStatusChoices';
import roles from '@/constants/roles';
import DeleteForm from '@/components/shared/widgets/DeleteForm.vue';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import { workOrders } from '@/aq-data-cy.json';
import WorkOrderExtractToCsv from '@/views/workOrders/WorkOrderExtractToCsv';
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import perpage from '@/constants/pagination';

import { isEmpty } from 'lodash';
import moment from 'moment';
import { mapGetters } from 'vuex';

export default {
    components: {
        TableRemote,
        SearchWithFilters,
    },
    data() {
        return {
            lockedByName: '',
            lockedById: '',
            filterName: 'workOrder',
            workOrders: [],
            loading: false,
            initialLoading: false,
            pagination: {},
            queryParams: {
                site: this.$route.params.siteId,
                excludeActivities: true,
                perpage,
                search: this.search,
                completionStatus: '',
                dateFrom: moment().subtract(30, 'days').toISOString(),
                dateTo: moment().toISOString(),
            },
            curentItem: null,
        };
    },
    computed: {
        filters() {
            return [
                {
                    key: 'type',
                    label: 'forms.labels.status',
                    type: 'select',
                    multiple: true,
                    items: this.completionStatusChoices,
                    datacy: 'WorkOrderFilterStatus',
                },
                {
                    key: 'date',
                    label: 'forms.labels.startDate',
                    type: 'date',
                    multiple: true,
                    items: this.completionStatusChoices,
                    value: [moment().subtract(30, 'days').toISOString(), moment().toISOString()],
                    dateKeys: ['createdDateFrom', 'createdDateTo'],
                    isRange: true,
                    datacy: 'WorkOrderFilterDate',
                },
            ];
        },
        ...mapGetters([
            'hasPermission',
            'hasRole',
            'teamDateFormat',
            'teamID',
        ]),
        status() {
            return completionStatus;
        },
        completionStatusChoices() {
            return Object.values(completionStatus).map(status => ({
                text: this.$t(`forms.labels.${status}`),
                value: status,
                isAlreadyTranslated: true,
            }));
        },
        roles() {
            return roles;
        },
        headers() {
            const mobileHeaders = [
                'startDate',
                'completionStatus',
                'workHours',
            ];

            const headers = [
                'startDate',
                'contractNumber',
                'workHours',
                'assignedTo',
                'poNumber',
                'soNumber',
                'associatedMeasures',
                'completionStatus',
            ];

            // Site's work orders & Field services' work orders
            if (!(this.$route.params.clientId || this.$route.params.siteId)) {
                headers.unshift('siteName');
                mobileHeaders.unshift('siteName');
            }

            headers.unshift('locked');
            mobileHeaders.unshift('locked');

            return this.$vuetify.breakpoint.mdAndDown ? mobileHeaders : headers;
        },
        tableHeaders() {
            return this.headers.map(header => ({
                text: this.$t(`visits.labels.${header}`),
                value: header,
            }));
        },
        sortableColumns() {
            return this.headers.map(column => this.$options.filters.lowercase(column));
        },
        displayedWorkOrders() {
            return this.workOrders.map(workOrder => ({
                siteName: `[${workOrder.site.deliveryAccountNumber}] ${workOrder.site.name}`,
                siteId: workOrder.site.ref,
                id: workOrder.id,
                jobName: workOrder.jobName,
                startDate: this.$options.filters.formatDate(workOrder.startDate),
                nodeType: workOrder.rootNode ? workOrder.rootNode.nodeType : null,
                contractNumber: workOrder.contractNumber || '-',
                completionStatus: workOrder.completionStatus || null,
                clientId: workOrder.site.client.ref || undefined,
                clientName: `[${workOrder.site.client.accountNumber}] ${workOrder.site.client.name}` || undefined,
                assignedTo: this.getAssignedTo(workOrder.assignedTo),
                poNumber: workOrder.poNumber || null,
                soNumber: workOrder.soNumber || null,
                onSiteDurationHours: workOrder.onSiteDurationHours,
                locked: workOrder?.lockData?.isLocked || false,
                lockedBy: workOrder?.lockData?.lockedBy || '',
                hasAnalysisRequest: workOrder.hasAnalysisRequest,
                hasInventory: workOrder.hasInventory,
                hasFlowMeter: workOrder.hasFlowMeter,
                hasProductLevel: workOrder.hasProductLevel,

            }));
        },
        actions() {
            const actions = [];

            if (this.hasPermission('workOrders.edit')) {
                actions.push({
                    name: this.$t('forms.btn.edit'),
                    icon: 'edit',
                    mode: 'edit',
                    customValidationProperty: 'locked',
                    showOncustomValidationProperty: false,
                });
            }
            if (this.hasPermission('workOrders.remove')) {
                actions.push({
                    name: this.$t('forms.btn.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                    customValidationProperty: 'locked',
                    showOncustomValidationProperty: false,

                });
            }
            if (this.hasPermission('workOrders.edit')) {
                actions.push({
                    name: this.$t('workOrders.unlock'),
                    icon: 'lock_open',
                    mode: 'unlock',
                    customValidationProperty: 'locked',
                    showOncustomValidationProperty: true,

                });
            }


            return actions;
        },
        showExtractCsvBtn() {
            if (!(this.$route.params.clientId || this.$route.params.siteId)) {
                return this.hasPermission('workOrders.export');
            }

            return null;
        },
        dataCy() {
            return {
                workOrders,
            };
        },
        isInWorkOrderSitePage() {
            return this.$route.name === 'siteWorkOrders';
        },
    },
    watch: {
        $route: {
            async handler() {
                if (this.$route.name === 'siteWorkOrders') {
                    this.initialLoading = true;
                    this.workOrders = [];

                    this.queryParams = {
                        site: this.$route.params.siteId,
                        excludeActivities: true,
                        perpage,
                        search: this.search,
                        completionStatus: '',
                    };
                    await this.getWorkOrders();
                    this.initialLoading = false;
                }
            },
        },
    },

    async created() {
        this.initialLoading = true;
        await this.getWorkOrders();
        this.initialLoading = false;
    },
    methods: {
        async getWorkOrders() {
            if (!this.loading) {
                this.loading = true;
                const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
                // for first time fetch Or when the requested page never fetched
                if (isEmpty(this.pagination) || (this.pagination?.currentPage !== this.queryParams?.page)) {
                    const { data } = await this.$rq.getWorkOrders(cleanQueryParams);
                    this.workOrders = [...this.workOrders, ...data.workOrders];
                    this.pagination = data.pagination;
                }
                this.loading = false;
            }
        },
        getAssignedTo(user) {
            if (!user.length) {
                return null;
            }
            const [assignedTo] = user;
            const { firstName, lastName } = assignedTo.user.profile;

            return `${firstName} ${lastName}`;
        },
        onClickAction(item, action) {
            if (action.mode === 'edit') {
                this.$router.push({
                    name: 'WorkOrderView',
                    params: {
                        clientId: item.clientId,
                        siteId: item.siteId,
                        workOrderId: item.id,
                        isEdition: true,
                    },
                });
            } else if (action.mode === 'delete') {
                this.removeWorkOrder(item);
            } else if (action.mode === 'unlock') {
                this.unlockWorkOrder(item);
            }
        },
        async removeWorkOrder(item) {
            const unlinkedAnalysisRequests = await this.getWoUnlinkedAnalysisRequest(item.id);

            this.$root.$emit('showModal', {
                title: this.$t('workOrder.remove').toUpperCase(),
                component: Vue.component('DeleteForm', DeleteForm),
                buttonTitle: 'delete',
                payload: {
                    msg: this.$t('workOrder.msg.remove'),
                    security: this.$t('workOrder.msg.security'),
                    analysisRequestMsg: this.$t('workOrder.msg.analysisRequest', { data: unlinkedAnalysisRequests }),
                    unlinkedAnalysisRequests,
                },
                isForm: false,
                onConfirm: async () => {
                    await this.$rq.deleteWorkOrder(item.id);
                    this.$toasted.global.toast_success({
                        message: this.$t('msg.workOrderDeleted', { data: item.contractNumber }),
                    });
                    this.reload();
                },
            });
        },
        async getWoUnlinkedAnalysisRequest(workOrder) {
            let unlinkedAnalysisRequests = [];
            const { data } = await this.$rq.getAnalysisRequests({ workOrder });
            const { analysisRequests } = data;
            if (analysisRequests.length) {
                unlinkedAnalysisRequests = analysisRequests.filter(
                    analysisRequest => !analysisRequest?.labWorkOrder || isEmpty(analysisRequest.labWorkOrder),
                ).map(an => (an.requestNumber));
            }

            return unlinkedAnalysisRequests.join();
        },
        async unlockWorkOrder(item) {
            this.$root.$emit('showModal', {
                title: this.$t('workOrders.unlock').toUpperCase(),
                component: Vue.component('ConfirmForm', ConfirmForm),
                payload: { msg: this.$t('workOrder.msg.unlock') },
                isForm: false,
                onConfirm: async () => {
                    await this.$rq.unlockWorkOrder(item.id);
                    this.$toasted.global.toast_success({
                        message: this.$t('msg.workOrderModified', { data: item.contractNumber }),
                    });
                    return this.getWorkOrders();
                },
            });
        },
        onClickRedirect(workOrder) {
            this.$router.push({
                name: 'WorkOrderView',
                params: {
                    workOrderId: workOrder.id,
                    clientId: workOrder.clientId,
                    siteId: workOrder.siteId,
                },
            });
        },
        // Search
        async fetchQuery(queryParams) {
            this.queryParams = {
                ...this.queryParams,
                ...queryParams,
                page: 1,
                site: this.isInWorkOrderSitePage ? this.$route.params.siteId : null,
                createdDateFrom: this.queryParams.createdDateFrom || moment.tz('UTC').subtract(30, 'days').toISOString(),
                createdDateTo: this.queryParams.createdDateTo ? moment.tz(this.queryParams.createdDateTo, 'UTC').endOf('day').toISOString() : moment.tz('UTC').endOf('day').toISOString(),
            };
            this.pagination = {};
            this.workOrders = [];
            await this.getWorkOrders();
        },
        handleSearch(value) {
            this.queryParams.search = value;
            this.fetchQuery();
        },
        async workOrdersChanged(queryParams) {
            if (queryParams.reset) {
                this.resetQueryParams();
            } else {
                this.queryParams = {
                    page: 1,
                    search: this.queryParams.search,
                    completionStatus: !queryParams.type ? '' : queryParams.type.join(','),
                    createdDateFrom: queryParams.createdDateFrom || null,
                    createdDateTo: queryParams.createdDateTo || null,
                    site: this.isInWorkOrderSitePage ? this.$route.params.siteId : null,
                };
            }
            this.fetchQuery();
        },
        onExtractToCsvClick() {
            this.$root.$emit('showModal', {
                title: this.$t('workOrders.exportWorkOrder'),
                component: Vue.component('GraphicForm', WorkOrderExtractToCsv),
                isForm: false,
                onConfirm: requestPayload => {
                    if (requestPayload) {
                        this.exportSiteAnalysisCSV(requestPayload);
                    }
                },
            });
        },
        async showLockedBy(event) {
            if (event.lockedBy !== this.lockedById) {
                const { data } = await this.$rq.getMemberInfo(event.lockedBy);
                this.lockedByName = `${data.firstName} ${data.lastName}`;
                this.lockedById = event.lockedBy;
            }
        },
        changeFilter() {
            this.queryParams.page = 1;
            this.workOrders = [];
            this.pagination = {};
            this.getWorkOrders();
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getWorkOrders();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
        async reload() {
            this.pagination = {};
            this.workOrders = [];
            this.queryParams.page = 1;
            await this.getWorkOrders();
        },
        async exportSiteAnalysisCSV(payload) {
            const startDate = moment(payload.dateRange[0]).startOf('day').toISOString();
            const endDate = moment(payload.dateRange[1] || new Date()).endOf('day').toISOString();
            const { accessGroups } = payload;
            const response = await this.$rq.exportWorkOrdersToCSV({ startDate, endDate, accessGroups });
            if (response.data && !response.data.size) {
                this.$toasted.global.toast_error({
                    message: this.$t('exportAnalysis.noResults'),
                });
            } else {
                const fileName = `workOrders_${startDate.split('T')[0]}_to_${endDate.split('T')[0]}.csv`;
                this.$utils.saveFileOnDisk(new Blob([response.data]), fileName);
            }
        },
        resetQueryParams() {
            this.queryParams = {
                site: this.$route.params.siteId,
                excludeActivities: true,
                perpage,
                search: '',
                completionStatus: '',
                dateFrom: moment().subtract(30, 'days').toISOString(),
                dateTo: moment().toISOString(),
            };
        },
    },
};
</script>
<style lang="scss">
    .export-btn{
        color: white !important;
        position: absolute;
        right: 11px;
        top: 10px;
    }
    @media screen and (max-width: 600px) {
        .export-btn{
            position: static
        }
    }

    .iconAssociatedMeasures {
        width: 20px;
        height: 20px;
        margin-right: 5px;
    }

</style>
